import siteDesignApi from '../apis/panel/site_design_api';
import i18n from '../plugins/i18n';
import { buildEntityRenamePayload } from '../features/panel-planning/utils/panelEntityNumbering.js';

/**
 * Array 관리를 위한 서비스 클래스
 * 역할: Array 생성, 삭제, 업데이트 등의 비즈니스 로직 처리
 */
export class ArrayManagementService {
  constructor() {
    this.siteId = null;
  }

  /**
   * 서비스 초기화
   * @param {string} siteId - 사이트 ID
   */
  initialize(siteId) {
    this.siteId = siteId;
    console.log('🔧 ArrayManagementService 초기화:', siteId);
  }

  /**
   * 새로운 Array 생성
   * @param {Object} arrayData - Array 데이터
   * @returns {Promise<Object>} 생성된 Array 정보
   */
  async createArray(arrayData, sectionId = null) {
    if (!this.siteId) {
      throw new Error(i18n.t('common.siteNotSelected'));
    }

    try {
      console.log('📤 Array 생성 요청:', arrayData);

      const response = await siteDesignApi.addSolarArray(this.siteId, arrayData, sectionId);

      console.log('✅ Array 생성 성공:', response);
      return response;
    } catch (error) {
      console.error('❌ Array 생성 실패:', error);
      throw error;
    }
  }

  /**
   * Array 삭제
   * @param {string} arrayId - 삭제할 Array ID
   * @returns {Promise<Object>} 삭제 결과
   */
  async deleteArray(arrayId) {
    if (!this.siteId) {
      throw new Error(i18n.t('common.siteNotSelected'));
    }

    try {
      console.log('🗑️ Array 삭제 요청:', arrayId);

      const response = await siteDesignApi.deleteSolarArray(this.siteId, arrayId);

      console.log('✅ Array 삭제 성공:', response);
      return response;
    } catch (error) {
      console.error('❌ Array 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * Array 이름 변경
   * @param {string} arrayId - 변경할 Array ID
   * @param {string} newName - 새로운 이름
   * @returns {Promise<Object>} 변경 결과
   */
  async updateArrayName(arrayId, newName) {
    if (!this.siteId) {
      throw new Error(i18n.t('common.siteNotSelected'));
    }

    try {
      console.log('✏️ Array 이름 변경 요청:', arrayId, '→', newName);

      const trimmed = (newName || '').trim();
      const payload = buildEntityRenamePayload('array', trimmed);
      if (!payload.name && !payload.arrayId) {
        throw new Error(i18n.t('common.arrayNameRequired'));
      }

      const response = await siteDesignApi.updateSolarArray(this.siteId, arrayId, payload);

      console.log('✅ Array 이름 변경 성공:', response);
      return response;
    } catch (error) {
      console.error('❌ Array 이름 변경 실패:', error);
      throw error;
    }
  }

  /**
   * Array 데이터 검증
   * @param {Object} arrayData - 검증할 Array 데이터
   * @returns {boolean} 검증 결과
   */
  validateArrayData(arrayData) {
    if (!arrayData.name || arrayData.name.trim() === '') {
      throw new Error(i18n.t('common.arrayNameRequired'));
    }

    if (arrayData.name.length > 100) {
      throw new Error(i18n.t('common.arrayNameMaxLength'));
    }

    return true;
  }

  /**
   * Array 데이터 정규화
   * @param {Object} arrayData - 정규화할 Array 데이터
   * @returns {Object} 정규화된 Array 데이터
   */
  normalizeArrayData(arrayData) {
    return {
      name: arrayData.name.trim(),
      description: arrayData.description || ''
      // 추가 필드들은 필요에 따라 확장
    };
  }
}

// 싱글톤 인스턴스 생성
export const arrayManagementService = new ArrayManagementService();
